import { Button, Grid2, IconButton, Link, Skeleton, Stack, Typography } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import ChevronRightIcon from '@/components/Icons/ChevronRightIcon';
import PlusIcon from '@/components/Icons/PlusIcon';
import { links } from '@/localization';
import { BasetenPageEnum, usePageSwitcher } from '@/routes';

import { useOrgSharedEndpoints } from '../../ModelAPIs/hooks';
import { WorkspaceSections } from '../../types';
import { AppLink, EmptyState, ModelAPIAnimationContainer, Section, ViewAllItems } from '../styled';
import Card from './Card';

const MAX_ITEMS_TO_SHOW = 6;

function ModelAPIs() {
  const goToPage = usePageSwitcher();
  const { orgSharedEndpoints, loading } = useOrgSharedEndpoints(true);

  function goToCreateModelAPIPage() {
    goToPage(BasetenPageEnum.CreateSharedEndpoint);
  }

  function getContent() {
    if (loading) {
      return (
        <Stack height={144}>
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="20%" />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="40%" />
        </Stack>
      );
    }

    if (isEmpty(orgSharedEndpoints)) {
      return (
        <EmptyState gap={8}>
          <Stack gap={3}>
            <Stack gap={1} maxWidth={600}>
              <Typography variant="subtitle1">Get started with Model APIs</Typography>
              <Typography variant="body1" color="text.muted">
                Run OpenAI-compatible frontier models on demand, optimized for speed and efficiency
                on the Baseten Inference Stack.{' '}
                <Link href={links.basetenDocsModelAPIs} target="_blank">
                  Learn more
                </Link>
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              onClick={goToCreateModelAPIPage}
              startIcon={<PlusIcon />}
              sx={{ width: 'fit-content' }}
            >
              Explore Model API
            </Button>
          </Stack>
          <ModelAPIAnimationContainer />
        </EmptyState>
      );
    }

    return (
      <Stack gap={2}>
        <Grid2 container spacing={2}>
          {orgSharedEndpoints.slice(0, MAX_ITEMS_TO_SHOW).map((orgSharedEndpoint) => {
            return (
              <Grid2
                size={{
                  sm: 12,
                  md: 6,
                }}
                key={orgSharedEndpoint.id}
              >
                <AppLink
                  page={BasetenPageEnum.SharedEndpoint}
                  params={{ sharedEndpointId: orgSharedEndpoint.id }}
                >
                  <Card orgSharedEndpoint={orgSharedEndpoint} />
                </AppLink>
              </Grid2>
            );
          })}
        </Grid2>
        {orgSharedEndpoints.length > MAX_ITEMS_TO_SHOW && (
          <AppLink
            page={BasetenPageEnum.Workspace}
            params={{ section: WorkspaceSections.ModelAPIs }}
            sx={{ color: 'text.subtle' }}
          >
            <ViewAllItems>View all Model APIs</ViewAllItems>
          </AppLink>
        )}
      </Stack>
    );
  }

  return (
    <Section>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack gap={1} height={54}>
          <Typography variant="h3">Model APIs</Typography>
          {!isEmpty(orgSharedEndpoints) && (
            <Typography variant="body1" color="text.muted">
              Production-ready Model APIs ready in one click.
            </Typography>
          )}
        </Stack>
        <AppLink page={BasetenPageEnum.Workspace} params={{ section: WorkspaceSections.ModelAPIs }}>
          <IconButton data-cy="list-item-arrow">
            <ChevronRightIcon />
          </IconButton>
        </AppLink>
      </Stack>
      {getContent()}
    </Section>
  );
}

export default ModelAPIs;
